import React, { useEffect, useState, useCallback, JSX } from 'react';
import { View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  useAnimatedStyle,
  useSharedValue
} from 'react-native-reanimated';
import styled from 'styled-components/native';
import { P } from '../../../components/Typography';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import FormElementQuestion from './components/FormElementQuestion';
import { scheduleOnRN } from 'react-native-worklets';

const StyledSliderContainer = styled.View`
  width: 100%;
  height: 49px;
  align-self: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
`;

const StyledSliderTrackView = styled.View`
  position: absolute;
  height: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  start: 0;
  end: 0;
`;

const StyledSliderTrackLineView = styled.View`
  height: 100%;
  width: 100%;
`;

const StyledThumbView = styled.View<{ width?: number }>`
  height: 49px;
  width: ${p => (p.width ? p.width : 0)}px;
  background-color: #ffcaaa;
`;

const StyledPickerView = styled.View`
  height: 49px;
  width: 100%;
  border: 1px solid #5e51c3;
  border-radius: 8px;
  flex-direction: row;
`;

const StyledSliderPickerTextView = styled.View`
  width: ${100 / 11}%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledSliderPickerText = styled(P)`
  font-size: 19px;
`;

const StyledLabelView = styled.View`
  width: 100%;
  margin-top: 16px;
  flex-direction: row;
  justify-content: space-between;
`;

export type FormSliderConfig = {
  defaultValue?: number;
  minLabel?: string;
  maxLabel?: string;
};

const FormNumberPicker = <T extends object>({
  element,
  edit,
  disabled
}: FormElementProps<T, FormSliderConfig>) => {
  const { formValues, setFormValue } = useDynamicFormContext<
    T,
    FormSliderConfig
  >();
  const key = (element.dataSource as keyof T) || element.key;
  const { defaultValue, minLabel, maxLabel } = element.config || {};
  const max = 10;
  const min = 0;
  const step = 1;
  const [sliderW, setSliderW] = useState(0);
  const offsetX = useSharedValue(0);
  const translateX = useSharedValue(0);
  const [sliderDimensions, setSliderDimensions] = useState({
    left: 0,
    right: 0
  });
  const [sliderValue, setSliderValue] = useState<number>(
    formValues[key]
      ? isNaN(formValues[key] as any)
        ? defaultValue || min
        : +(formValues[key] as any)
      : defaultValue || min
  );
  const sliderTotalSteps = (max - min) / step;
  const commitNumberPickerValue = useCallback(
    (value: any) => {
      setFormValue(key, value, element);
    },
    [key, element]
  );

  const handleSliderState = useCallback(
    (translationX: number, fromTapEvent?: boolean) => {
      'worklet';
      const validTranslationX = sliderDimensions.right / sliderTotalSteps;
      let progress = Math.round(translationX / validTranslationX - 0.4);
      const newTranslationX = progress * validTranslationX;
      if (
        newTranslationX !== 0 &&
        newTranslationX > sliderDimensions.left &&
        newTranslationX < sliderDimensions.right
      ) {
        translateX.value = progress * validTranslationX;
      } else if (newTranslationX <= 0) {
        translateX.value = sliderDimensions.left;
      } else if (newTranslationX >= sliderDimensions.right) {
        translateX.value = sliderDimensions.right;
      }

      progress = progress * step;
      let value = min;
      if (progress < min) {
        value = min;
      } else if (progress >= max) {
        value = max;
      } else {
        value = progress + min;
      }
      if (value === sliderValue) return;
      scheduleOnRN(setSliderValue, value);
      if (fromTapEvent) {
        scheduleOnRN(commitNumberPickerValue, value);
      }
    },
    [
      element,
      key,
      max,
      min,
      setFormValue,
      sliderDimensions.left,
      sliderDimensions.right,
      sliderTotalSteps,
      sliderValue,
      step,
      translateX
    ]
  );

  useEffect(() => {
    if (sliderDimensions.right === 0) return;
    const sliderInitialValue = sliderValue;
    const validTranslationX = sliderDimensions.right / sliderTotalSteps;
    handleSliderState(
      ((sliderInitialValue as any) - min) * (1 / step) * validTranslationX
    );
  }, [
    handleSliderState,
    min,
    sliderDimensions,
    sliderTotalSteps,
    sliderValue,
    step
  ]);

  const panGesture = Gesture.Pan()
    .onStart(() => {
      offsetX.value = translateX.value;
    })
    .onUpdate(event => {
      const translationX = event.translationX + offsetX.value;
      handleSliderState(translationX);
    })
    .onFinalize((_, success) => {
      if (success) scheduleOnRN(commitNumberPickerValue, sliderValue);
    });

  const tapGesture = Gesture.Tap().onFinalize(event => {
    handleSliderState(event.x, true);
  });

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: translateX.value }]
    };
  });

  const renderOptions = () => {
    const rip: JSX.Element[] = [];
    for (let i = 0; i <= 10; i++) {
      rip.push(
        <StyledSliderPickerTextView key={String(key) + i}>
          <StyledSliderPickerText>{i}</StyledSliderPickerText>
        </StyledSliderPickerTextView>
      );
    }
    return rip;
  };

  const currentValue = formValues[key];
  useEffect(() => {
    if (defaultValue != null && currentValue == null) {
      setFormValue(key, (defaultValue || min) as any, element);
    }
  }, [defaultValue, min, currentValue, key, element, setFormValue]);
  return (
    <View>
      <FormElementQuestion element={element} />
      <StyledSliderContainer
        onLayout={event => {
          const { width } = event.nativeEvent.layout;
          setSliderW(width / 11);
          setSliderDimensions({
            left: 0,
            right: width - width / 11
          });
        }}
      >
        <GestureDetector gesture={tapGesture}>
          <StyledSliderTrackView>
            <StyledSliderTrackLineView />
          </StyledSliderTrackView>
        </GestureDetector>
        <GestureDetector gesture={panGesture}>
          <Animated.View style={[{ width: sliderW }, animatedStyle]}>
            <StyledThumbView width={sliderW} />
          </Animated.View>
        </GestureDetector>
        <StyledPickerView style={{ position: 'absolute' }} pointerEvents='none'>
          {renderOptions()}
        </StyledPickerView>
      </StyledSliderContainer>
      <StyledLabelView>
        <P>{minLabel || ''}</P>
        <P>{maxLabel || ''}</P>
      </StyledLabelView>
    </View>
  );
};

export const FormNumberPickerPlugin: FormElementPlugin<any, FormSliderConfig> =
  {
    name: 'Number Picker',
    type: 'NumberPicker',
    dataTypes: ['text', 'integer', 'numeric'],
    element: FormNumberPicker
  };
